import React, { useMemo, useState } from "react";
import { Form, Input, Select, Avatar, Switch, Card, Divider, Row, Col, Alert, Typography, Button, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { useAuth } from "../../hooks/useAuth";
import { IBrand, ISurveyCohort, ISurveyTheme } from "../../services/nestjs-base-api/types";
import { TemplateTypeOptions } from "../../enums/TemplateTypeEnum";
import { COUNTRY_OPTIONS, LANGUAGE_OPTIONS, renderFlagIcon, getCountryName } from "../../constants/countries";
import { useCreateBrandCohortMutation } from "../../services/nestjs-base-api";
import CohortFormModal from "../Cohorts/CohortFormModal";
import { WelcomePreview } from './WelcomePreview';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface BasicInfoStepProps {
    formData: {
        name: string;
        brandId: number;
        cohortId: number;
        countryCode?: string;
        languageCode?: string;
        description?: string;
        showWelcomePage: boolean;
        theme?: ISurveyTheme;
    };
    brands: IBrand[];
    brandsLoading: boolean;
    cohorts: ISurveyCohort[];
    cohortsLoading: boolean;
    errors: Record<string, string>;
    onChange: (updates: any) => void;
    onBrandTemplateTypeUpdate?: (brandId: number, templateType: string) => void;
    onCohortCreated?: () => void;
    isEditMode?: boolean;
    hideProductTypeSelection?: boolean;
    hideBrandSelector?: boolean;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
    formData,
    brands,
    brandsLoading,
    cohorts,
    cohortsLoading,
    errors,
    onChange,
    onBrandTemplateTypeUpdate,
    onCohortCreated,
    isEditMode = false,
    hideProductTypeSelection = false,
    hideBrandSelector = false
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    const [cohortModalVisible, setCohortModalVisible] = useState(false);
    const [createCohort] = useCreateBrandCohortMutation();

    // Check if selected brand needs template type
    const selectedBrand = useMemo(() => {
        return brands.find(brand => brand.id === formData.brandId);
    }, [brands, formData.brandId]);

    const showTemplateTypePrompt = selectedBrand && !selectedBrand.templateType && !hideProductTypeSelection && !isEditMode;

    // Filter system cohorts for new cohort parent selection
    const systemCohorts = useMemo(() => {
        return cohorts.filter((c: ISurveyCohort) => c.cohortType === 'system');
    }, [cohorts]);

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ name: e.target.value });
    };

    const handleCreateNewCohort = () => {
        if (!formData.brandId) {
            message.warning('Please select a brand first');
            return;
        }
        setCohortModalVisible(true);
    };

    const handleCohortFormSubmit = async (values: any) => {
        if (!formData.brandId) return;

        try {
            const newCohort = await createCohort({
                jwtToken: user?.jwtToken!,
                brandId: formData.brandId,
                data: values
            }).unwrap();

            message.success('Cohort created successfully');
            setCohortModalVisible(false);

            // Trigger refresh in parent component
            if (onCohortCreated) {
                onCohortCreated();
            }

            // Auto-select the newly created cohort
            onChange({ cohortId: newCohort.id });
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to create cohort');
            throw error;
        }
    };

    const handleBrandChange = (value: number) => {
        onChange({ brandId: value });
    };

    const handleCohortChange = (value: number) => {
        onChange({ cohortId: value });
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange({ description: e.target.value });
    };

    const handleShowWelcomePageChange = (checked: boolean) => {
        onChange({ showWelcomePage: checked });
    };

    const handleCountryChange = (value: string) => {
        onChange({ countryCode: value });
    };

    const handleLanguageChange = (value: string) => {
        onChange({ languageCode: value });
    };

    const handleTemplateTypeSelect = (templateType: string) => {
        if (selectedBrand && onBrandTemplateTypeUpdate) {
            onBrandTemplateTypeUpdate(selectedBrand.id, templateType);
        }
    };

    return (
        <div>
            <h3>{intl.formatMessage({ id: 'survey.create.step1.title' })}</h3>
            <p style={{ color: '#6b7280', marginBottom: 24 }}>
                {intl.formatMessage({ id: 'survey.create.step1.subtitle' })}
            </p>

            <Row gutter={24}>
                <Col xs={24}>
                    <Form layout="vertical">
                <Form.Item
                    label="Survey Title"
                    validateStatus={errors.name ? 'error' : ''}
                    help={errors.name}
                    required
                >
                    <Input
                        value={formData.name}
                        onChange={handleNameChange}
                        placeholder="Enter survey title"
                        size="large"
                        maxLength={100}
                        showCount
                    />
                </Form.Item>

                {!hideBrandSelector && (
                    <Form.Item
                        label={intl.formatMessage({ id: 'survey.form.brand' })}
                        validateStatus={errors.brandId ? 'error' : ''}
                        help={errors.brandId}
                        required
                    >
                        <Select
                            value={formData.brandId || undefined}
                            onChange={handleBrandChange}
                            placeholder={intl.formatMessage({ id: 'survey.form.brandPlaceholder' })}
                            size="large"
                            loading={brandsLoading}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children?.toString().toLowerCase().includes(input.toLowerCase())) ?? false
                            }
                        >
                            {brands.map(brand => (
                                <Option key={brand.id} value={brand.id}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {brand.logoUrl ? (
                                            <Avatar
                                                src={brand.logoUrl}
                                                size="small"
                                                style={{ backgroundColor: brand.primaryColor }}
                                            >
                                                {brand.name.charAt(0).toUpperCase()}
                                            </Avatar>
                                        ) : (
                                            <Avatar
                                                size="small"
                                                style={{ backgroundColor: brand.primaryColor }}
                                            >
                                                {brand.name.charAt(0).toUpperCase()}
                                            </Avatar>
                                        )}
                                        <span>{brand.name}</span>
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                {showTemplateTypePrompt && (
                    <Alert
                        message="Product Type Required"
                        description={
                            <div style={{ marginTop: 8 }}>
                                <Text style={{ marginBottom: 8, display: 'block' }}>
                                    To create accurate surveys for "{selectedBrand.name}", please select the type of products this brand sells:
                                </Text>
                                <Select
                                    placeholder="What type of products do you sell?"
                                    style={{ width: '100%' }}
                                    size="large"
                                    onChange={handleTemplateTypeSelect}
                                    options={TemplateTypeOptions}
                                />
                            </div>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Country"
                            tooltip="Select the country where this survey will be conducted. Used for benchmarking and regional analysis."
                            required
                        >
                            <Select
                                value={formData.countryCode || 'AU'}
                                onChange={handleCountryChange}
                                placeholder="Select country"
                                size="large"
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.children?.toString().toLowerCase().includes(input.toLowerCase())) ?? false
                                }
                            >
                                {COUNTRY_OPTIONS.map(country => (
                                    <Option key={country.code} value={country.code}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                            {renderFlagIcon(country.code, 20)}
                                            <span>{country.name}</span>
                                        </span>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Language"
                            tooltip="Currently only English surveys are supported. Multi-language surveys will be available in a future version."
                            required
                        >
                            <Select
                                value={formData.languageCode || 'en'}
                                onChange={handleLanguageChange}
                                placeholder="Select language"
                                size="large"
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.children?.toString().toLowerCase().includes(input.toLowerCase())) ?? false
                                }
                            >
                                {LANGUAGE_OPTIONS.map(language => (
                                    <Option
                                        key={language.code}
                                        value={language.code}
                                        disabled={language.code !== 'en'}
                                    >
                                        {language.name} {language.code !== 'en' && '(Coming Soon)'}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    label="Customer Cohort"
                    validateStatus={errors.cohortId ? 'error' : ''}
                    help={errors.cohortId}
                    required
                >
                    <Select
                        value={formData.cohortId || undefined}
                        onChange={handleCohortChange}
                        placeholder="Select customer cohort"
                        size="large"
                        loading={cohortsLoading}
                        disabled={cohortsLoading || isEditMode || (!!formData.brandId && cohorts.length === 0)}
                        dropdownRender={(menu) => (
                            <>
                                {menu}
                                {!isEditMode && formData.brandId && (
                                    <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                                        <Button
                                            type="link"
                                            icon={<PlusOutlined />}
                                            onClick={handleCreateNewCohort}
                                            style={{ width: '100%', textAlign: 'left' }}
                                        >
                                            Create New Cohort
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    >
                        {cohorts.map(cohort => (
                            <Option key={cohort.id} value={cohort.id}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, lineHeight: '1.4' }}>
                                    <span style={{ fontWeight: 500, fontSize: '14px' }}>{cohort.displayName || cohort.name}</span>
                                    <span style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.3' }}>
                                        {cohort.description}
                                    </span>
                                </div>
                            </Option>
                        ))}
                    </Select>
                    {!isEditMode && formData.brandId && cohorts.length === 0 && !cohortsLoading && (
                        <Alert
                            message="All cohorts in use"
                            description={`This brand already has active surveys for all available cohorts in ${getCountryName(formData.countryCode || 'AU') || formData.countryCode || 'Australia'}. Please choose a different country or complete or pause an existing survey before creating a new one.`}
                            type="info"
                            style={{ marginTop: 8 }}
                            showIcon
                        />
                    )}
                </Form.Item>

                <Form.Item
                    label={intl.formatMessage({ id: 'survey.form.description' })}
                >
                    <TextArea
                        value={formData.description}
                        onChange={handleDescriptionChange}
                        placeholder={intl.formatMessage({ id: 'survey.form.descriptionPlaceholder' })}
                        rows={4}
                        maxLength={500}
                        showCount
                    />
                </Form.Item>

                <Divider style={{ margin: '32px 0' }} />

                <Form.Item label={intl.formatMessage({ id: 'survey.settings.showWelcomePage' })}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Switch
                            checked={formData.showWelcomePage}
                            onChange={handleShowWelcomePageChange}
                        />
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>
                            {intl.formatMessage({ id: 'survey.settings.showWelcomePageDesc' })}
                        </span>
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '12px', marginTop: 4 }}>
                        {intl.formatMessage({ id: 'survey.settings.showWelcomePageNote' })}
                    </div>
                </Form.Item>
            </Form>
                </Col>

                {/* Welcome Page Preview */}
                {formData.showWelcomePage && (
                    <Col xs={24}>
                        <Card
                            title={intl.formatMessage({ id: 'survey.settings.preview' })}
                            style={{ width: '100%', marginTop: 16 }}
                        >
                            <WelcomePreview
                                title={formData.name || 'Survey Title'}
                                subtitle={formData.description || undefined}
                                theme={formData.theme}
                            />
                        </Card>
                    </Col>
                )}
            </Row>

            <CohortFormModal
                visible={cohortModalVisible}
                cohort={null}
                systemCohorts={systemCohorts}
                onCancel={() => setCohortModalVisible(false)}
                onSubmit={handleCohortFormSubmit}
            />
        </div>
    );
};

export default BasicInfoStep;